/**
 * Permissions Configuration
 * 
 * This file defines which navigation links/apps are shown to which user types.
 * 
 * Permission Rules (in plain English):
 * 
 * 1. DEBUG APPS: Only shown to user ID 1 (super admin) - includes all other apps plus debug apps
 * 
 * 2. CREW APPS: Only shown to crew-only users (users who have crew role but no other roles)
 * 
 * 3. FIELD APPS: Shown to everyone except crew-only users 
 *    (field workers, managers, staff, executives)
 * 
 * 4. MANAGER APPS: Shown to managers and executives only
 * 
 * 5. OFFICE STAFF APPS: Shown to staff, managers, and executives
 * 
 * 6. EXECUTIVE APPS: Shown only to executive users
 * 
 * The order of app categories in this configuration determines the rendering order
 * on the Home page.
 */

/**
 * Determines if a user is crew-only (has crew role but no other roles)
 * @param {Object} user - User object with role properties
 * @returns {boolean} - True if user is crew-only
 */
const isCrewOnly = (user) => {
  return user.crew && !(user.executive || user.manager || user.staff);
};

/**
 * Configuration object that maps app categories to their permission requirements
 * Each category has:
 * - title: Display title for the app section
 * - gridCols: CSS grid columns for layout (1, 2, or 3 columns)
 * - shouldShow: Function that determines if this category should be shown to the user
 * - getApps: Function that returns the apps for this category
 */
export const APP_PERMISSIONS_CONFIG = {
  // Debug apps - only for super admin (user ID 1)
  debug: {
    title: "Debug",
    gridCols: "md:grid-cols-2",
    shouldShow: (user) => user.id === 1,
    getApps: (appLinks) => appLinks.debug || []
  },

  // Crew apps - only for crew-only users
  crew: {
    title: "Crew", 
    gridCols: "md:grid-cols-2",
    shouldShow: (user) => isCrewOnly(user),
    getApps: (appLinks) => appLinks.crew || []
  },

  // Field apps - shown to everyone except crew-only users
  field: {
    title: "Field",
    gridCols: "md:grid-cols-3", 
    shouldShow: (user) => !isCrewOnly(user),
    getApps: (appLinks) => appLinks.field || []
  },

  // Manager apps - shown to managers and executives
  manager: {
    title: "Manager",
    gridCols: "md:grid-cols-2",
    shouldShow: (user) => user.manager || user.executive,
    getApps: (appLinks, managerLinksWithNotifications) => managerLinksWithNotifications || appLinks.manager || []
  },

  // Office staff apps - shown to staff, managers, and executives  
  staff: {
    title: "Office",
    gridCols: "md:grid-cols-3",
    shouldShow: (user) => user.staff || user.manager || user.executive,
    getApps: (appLinks) => appLinks.staff || []
  },

  // Executive apps - shown only to executives
  executive: {
    title: "Executive", 
    gridCols: "md:grid-cols-1",
    shouldShow: (user) => user.executive,
    getApps: (appLinks) => appLinks.executive || []
  }
};

/**
 * Gets the visible app categories for a given user
 * @param {Object} user - User object with role properties
 * @param {Object} appLinks - App links configuration from navConfig
 * @param {Array} managerLinksWithNotifications - Manager links with notification data
 * @returns {Array} - Array of visible app categories with their data
 */
export const getVisibleAppCategories = (user, appLinks, managerLinksWithNotifications = null) => {
  const visibleCategories = [];

  // Special case: Super admin (user ID 1) gets everything including debug
  if (user.id === 1) {
    // Add all categories for super admin
    Object.entries(APP_PERMISSIONS_CONFIG).forEach(([key, config]) => {
      const apps = config.getApps(appLinks, managerLinksWithNotifications);
      if (apps.length > 0) {
        visibleCategories.push({
          key,
          title: config.title,
          gridCols: config.gridCols,
          apps
        });
      }
    });
    return visibleCategories;
  }

  // For all other users, check permissions normally (excluding debug)
  Object.entries(APP_PERMISSIONS_CONFIG).forEach(([key, config]) => {
    // Skip debug apps for non-super-admin users
    if (key === 'debug') return;
    
    if (config.shouldShow(user)) {
      const apps = config.getApps(appLinks, managerLinksWithNotifications);
      if (apps.length > 0) {
        visibleCategories.push({
          key,
          title: config.title, 
          gridCols: config.gridCols,
          apps
        });
      }
    }
  });

  return visibleCategories;
};
